import React from 'react';

import { CardSkeletonLoader } from '@pkg/components/Common/CardTile/CardSkeletonLoader';
import { useAppSelector, useMedia } from '@pkg/hooks';
import { useMediaQuery } from '@pkg/providers';

import { usePodsNumberOfItems } from '@app/pages/Groups/GroupItem';
import { NftWrapper, NftWrapperGrid } from '@app/pages/Groups/styles';

import { CardGrid } from '../GroupGrid/sections/ModernGrid/components';

import {
    GroupAvatarSkeleton,
    GroupDescriptionSkeleton,
    GroupNameSkeleton,
    GroupSkeletonAvatar,
    GroupSkeletonHeader,
    GroupSkeletonList,
    GroupSkeletonName,
    GroupSkeletonSubscribeMobile,
    GroupSkeletonSubscribeSingle,
    GroupSkeletonSubscribeSkeleton,
    GroupSkeletonWidth,
    GroupSkeletonWrapper,
    SkeletonGroupAvatarSkeleton,
    SkeletonGroupDescriptionSkeleton,
    SkeletonGroupMembers,
    SkeletonGroupMembersDesktop,
    SkeletonGroupMemberSkeleton,
    SkeletonGroupMembersMobile,
    SkeletonGroupNameSkeleton,
} from './style';

export function GroupSkeleton() {
    const { showEvents } = useAppSelector((state) => state.featureFlags.features);

    const showSmallCards = useMedia(`(max-width: 926px)`);
    const showThisManyItems = usePodsNumberOfItems();

    return (
        <GroupSkeletonWidth>
            <GroupSkeletonWrapper>
                <GroupSkeletonHeader>
                    <GroupSkeletonAvatar>
                        <SkeletonGroupAvatarSkeleton isLoading />
                    </GroupSkeletonAvatar>

                    <GroupSkeletonName>
                        <SkeletonGroupNameSkeleton isLoading />
                        <SkeletonGroupMembersDesktop>
                            <SkeletonGroupDescriptionSkeleton isLoading />
                            <SkeletonGroupMembers>
                                <SkeletonGroupMemberSkeleton isLoading />
                                <SkeletonGroupMemberSkeleton isLoading />
                                <SkeletonGroupMemberSkeleton isLoading />
                            </SkeletonGroupMembers>
                        </SkeletonGroupMembersDesktop>
                    </GroupSkeletonName>
                </GroupSkeletonHeader>

                <SkeletonGroupMembersMobile>
                    <SkeletonGroupDescriptionSkeleton isLoading />
                    <SkeletonGroupMembers>
                        <SkeletonGroupMemberSkeleton isLoading />
                        <SkeletonGroupMemberSkeleton isLoading />
                        <SkeletonGroupMemberSkeleton isLoading />
                    </SkeletonGroupMembers>
                </SkeletonGroupMembersMobile>
            </GroupSkeletonWrapper>

            <NftWrapper $showEvents={showEvents}>
                <NftWrapperGrid $showThisManyItems={showThisManyItems}>
                    {Array.from({ length: showThisManyItems }, (_, i) => (
                        <CardSkeletonLoader
                            key={`pods-skeleton-card-${i}`}
                            cardSize={showSmallCards ? 'small' : undefined}
                        />
                    ))}
                </NftWrapperGrid>
            </NftWrapper>
        </GroupSkeletonWidth>
    );
}

export function GroupHeaderLoading() {
    return (
        <>
            <GroupSkeletonSubscribeMobile>
                <GroupSkeletonSubscribeSkeleton isLoading />
            </GroupSkeletonSubscribeMobile>

            <GroupSkeletonHeader>
                <GroupSkeletonAvatar>
                    <GroupAvatarSkeleton isLoading />
                </GroupSkeletonAvatar>

                <GroupSkeletonName>
                    <GroupNameSkeleton isLoading />

                    <SkeletonGroupMembersDesktop>
                        <GroupDescriptionSkeleton isLoading />
                        <SkeletonGroupMembers>
                            <SkeletonGroupMemberSkeleton isLoading />
                            <SkeletonGroupMemberSkeleton isLoading />
                            <SkeletonGroupMemberSkeleton isLoading />
                        </SkeletonGroupMembers>
                    </SkeletonGroupMembersDesktop>
                </GroupSkeletonName>

                <GroupSkeletonSubscribeSingle>
                    <GroupSkeletonSubscribeSkeleton isLoading />
                </GroupSkeletonSubscribeSingle>
            </GroupSkeletonHeader>

            <SkeletonGroupMembersMobile>
                <GroupDescriptionSkeleton isLoading />

                <SkeletonGroupMembers>
                    <SkeletonGroupMemberSkeleton isLoading />
                    <SkeletonGroupMemberSkeleton isLoading />
                    <SkeletonGroupMemberSkeleton isLoading />
                </SkeletonGroupMembers>
            </SkeletonGroupMembersMobile>
        </>
    );
}

export function GroupGridLoading() {
    const { widescreen } = useMediaQuery();
    const compact = !widescreen;

    return (
        <GroupSkeletonList>
            <CardGrid compact={compact}>
                {Array(20)
                    .fill(null)
                    .map((_, i) => (
                        <CardSkeletonLoader key={`Pod-Skeleton-${i}`} />
                    ))}
            </CardGrid>
        </GroupSkeletonList>
    );
}
